import React, { useState, useEffect } from "react";
import { BatchRemoveStakeForm } from "@/components/BatchRemoveStakeForm";
import {
  useAccount,
  useSwitchChain,
  useWriteContract,
  useBalance,
  usePublicClient,
} from "wagmi";
import { parseUnits } from "ethers";
import { encodeFunctionData, parseEventLogs } from "viem";
import { useNotify } from "@/components/NotificationProvider";
import { useTransactionReceipt } from "@/hooks/useTransactionReceipt";
import { STAKING_V2_ABI } from "@/const/abi";
import { STAKING_V2_ADDRESS } from "@/const/address";
import { getFailedType } from "@/utils/errors";
import style from "./index.module.less";
import { formatBalance } from "@/utils/formatters";

interface BatchRemoveStakeData {
  netuid: number;
  amount: string;
  slippage: string;
}

function RemoveStake() {
  const { chainId: connectedChainId, address } = useAccount();
  const {
    data: balanceData,
    isError: balanceIsError,
    error: balanceError,
  } = useBalance({
    address,
    chainId: 964, // Subtensor EVM chain ID
  });
  const { switchChain } = useSwitchChain();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const publicClient = usePublicClient();

  // Contract interaction hooks
  const {
    data: hash,
    writeContractAsync,
    isPending,
    isError: writeContractIsError,
    error: writeError,
  } = useWriteContract();

  const {
    isLoading: isConfirming,
    isSuccess,
    isError,
    data: receipt,
  } = useTransactionReceipt(hash, {
    enabled: !!loading,
    retryDelay: 5000,
    maxRetries: 10,
  });

  // Handle transaction status
  useEffect(() => {
    if (isSuccess && receipt) {
      setLoading(false);

      // Parse MulticallExecuted event to check actual execution results
      try {
        const logs = parseEventLogs({
          abi: STAKING_V2_ABI,
          logs: receipt.logs,
          eventName: "MulticallExecuted",
        });

        if (logs.length > 0) {
          const multicallEvent = logs[0] as any;
          const { callCount, successCount } = multicallEvent.args as {
            caller: string;
            callCount: bigint;
            successCount: bigint;
          };

          console.log("Multicall execution results:", {
            totalCalls: Number(callCount),
            successfulCalls: Number(successCount),
            failedCalls: Number(callCount) - Number(successCount),
          });

          if (Number(successCount) === Number(callCount)) {
            notify.success(
              "Batch remove stakes successfully",
              `All ${Number(callCount)} stakes have been removed successfully.`
            );
          } else {
            notify.warning(
              "Partial batch execution",
              `${Number(successCount)} out of ${Number(
                callCount
              )} stakes were removed successfully.`
            );
          }
        } else {
          notify.success(
            "Batch remove stakes successfully",
            "All stakes have been removed successfully in one transaction."
          );
        }
      } catch (error) {
        console.error("Error parsing MulticallExecuted event:", error);
        notify.success(
          "Batch remove stakes successfully",
          "All stakes have been removed successfully in one transaction."
        );
      }
    }
  }, [isSuccess, receipt, notify]);

  useEffect(() => {
    if (isError || writeContractIsError) {
      setLoading(false);
      const errorMsg = writeError?.message || "Transaction failed";
      const failureType = getFailedType(
        (errorMsg || String(errorMsg)).toLowerCase()
      );
      notify.error("Remove stake failed", failureType);
    }
  }, [isError, writeContractIsError, writeError, notify]);

  const handleSubmit = async (batchData: BatchRemoveStakeData[]) => {
    if (!batchData || batchData.length === 0) {
      notify.error(
        "Validation Error",
        "Please add at least one remove operation"
      );
      return;
    }
    console.log("Submitting batch stake data:", batchData);

    // Validate all batch items
    for (const item of batchData) {
      if (!item.amount || parseFloat(item.amount) <= 0) {
        notify.error(
          "Validation Error",
          "Please enter valid amounts for all remove operations"
        );
        return;
      }
      if (!item.netuid) {
        notify.error(
          "Validation Error",
          "Please select netuid for all remove operations"
        );
        return;
      }
    }

    setLoading(true);

    // Auto switch to correct chain if needed (assuming chain ID 964 for Subtensor EVM)
    if (connectedChainId && connectedChainId !== 964) {
      try {
        await switchChain({ chainId: 964 });
      } catch (error) {
        setLoading(false);
        notify.error(
          "Chain Switch Failed",
          "Please manually switch to Subtensor EVM network"
        );
        return;
      }
    }

    try {
      // Encode each removeStake call
      const encodedCalls: string[] = [];

      for (const item of batchData) {
        const alphaAmount = parseUnits(String(item.amount), 9);
        const netuid = BigInt(item.netuid);
        const slippage = BigInt(Math.floor((Number(item.slippage) || 1) * 10)); // Convert percentage to basis points
        const allowPartial = false; // Allow partial fulfillment

        // Encode the removeStake function call
        const encodedCall = encodeFunctionData({
          abi: STAKING_V2_ABI,
          functionName: "removeStake",
          args: [alphaAmount, netuid, slippage, allowPartial],
        });

        encodedCalls.push(encodedCall);
      }

      console.log("Calling multicall with batch remove data:", {
        batchCount: batchData.length,
        batchData,
        encodedCalls,
      });

      // Dynamic gas estimation with fallback for multicall
      let gasLimit = BigInt(200000 + batchData.length * 200000); // Dynamic base: 200k + 200k per operation
      // try {
      //   if (publicClient) {
      //     const estimatedGas = await publicClient.estimateContractGas({
      //       address: STAKING_V2_ADDRESS,
      //       abi: STAKING_V2_ABI,
      //       functionName: "multicall",
      //       args: [encodedCalls],
      //     });
      //     console.log("Estimated Gas for multicall:", estimatedGas);
      //     gasLimit = BigInt(Math.floor(Number(estimatedGas) * 1.2) + 200000); // Add 20% buffer
      //   }
      // } catch (gasEstimateError) {
      //   console.log(
      //     "Multicall gas estimation failed, using fallback:",
      //     gasEstimateError
      //   );
      // }

      console.log("Final gas limit for multicall:", gasLimit);

      // Call the multicall contract function with dynamic gas estimation
      await writeContractAsync({
        address: STAKING_V2_ADDRESS,
        abi: STAKING_V2_ABI,
        functionName: "multicall",
        args: [encodedCalls],
        gas: gasLimit,
      });

      // Transaction submitted, waiting for confirmation in useEffect
    } catch (error: any) {
      setLoading(false);
      console.error("Batch remove stake error details:", {
        error,
        message: error?.message,
        cause: error?.cause,
        details: error?.details,
      });

      let errorMessage = "Transaction failed";
      if (
        error?.message?.includes("Cannot destructure property 'gasLimit'") ||
        error?.message?.includes("execution fatal") ||
        error?.message?.includes("ModuleError")
      ) {
        errorMessage =
          "Insufficient stake balance or invalid parameters. Please check your stake balance.";
      } else {
        const failureType = getFailedType(
          (error?.message || String(error)).toLowerCase()
        );
        errorMessage = `${failureType}. Please try again.`;
      }

      notify.error("Batch remove stake failed", errorMessage);
    }
  };

  return (
    <div className={style.stake}>
      <div className={style.stake_title}>
        Batch Remove Stakes: {formatBalance(Number(balanceData?.value) / 1e18)}t
      </div>
      <div className={style.stake_desc}>
        Remove multiple stakes in one transaction - save on gas fees!
      </div>
      <BatchRemoveStakeForm
        onSubmit={handleSubmit}
        loading={loading}
        maxBalance={Number(balanceData?.value) / 1e18}
      />
    </div>
  );
}

export default RemoveStake;
